/**
 * React Context for vibesites runtime
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Property } from '@vibesites/schema';
import type { DesignTokens } from '@vibesites/tokens';
import { generateTokensFromProperty, generateCSSString, getContrastTextColor } from '@vibesites/tokens';
import type { 
  RuntimeContext, 
  SiteManifest, 
  ResolvedRegistry, 
  PageContext,
  CompanyData,
  TemplateContext,
  ContentOverridesMap,
  ComponentContentSchema,
  ContentTokenDefinition,
  DesignOverride,
  DesignOverridesMap,
  ComponentDesignSchema,
  SlotGroupDefinition,
  SlotGroupOverridesMap,
  CollectionBindingDefinition,
  CollectionBindingOverridesMap,
  ContentOverride,
  StyleOverride,
  StyleOverridesMap,
} from '../types';
import { resolveScopedOverride as resolveScopedOverrideV2, type ScopePageContext } from '../scope';
import { resolveContentTokenValue, resolveSlotGroupFields, type SlotGroupResult } from '../resolve';
import { applyCollectionBindingOverride } from '../collection-binding';
import { IS_IN_EDITOR_FRAME, useReportedRuntimeDefinition } from './runtime-definitions';
import { EventsProvider } from './events-context';
import { useLiveCollection } from './use-live-collection';

// ============================================================================
// Context Definition
// ============================================================================

const VibesitesContext = createContext<RuntimeContext | null>(null);

// Base spacing for richtext content. Tailwind's Preflight (`@tailwind base`)
// resets every <p> margin to 0, which makes multi-paragraph blurbs render as one
// solid block. We restore a modest gap, scoped to the richtext hook so it ONLY
// affects AI-blurb / richtext paragraphs — never theme-authored <p> elsewhere.
// Low specificity so a theme can override; last-child reset avoids a trailing
// gap on single-paragraph blurbs.
//
// Skip blank-line SPACER paragraphs (`<p></p>`, `<p><br></p>`). On the live site
// `collapseBlankParagraphs` (rich-text-content) removes these before render, so
// this is mainly a backstop for the editor surface and any spacer that slips
// through: zeroing their margin stops them compounding with the paragraph gap.
// (`:has` is modern; where unsupported a stray spacer just keeps its margin —
// extra space, never broken layout.)
const RICHTEXT_BASE_CSS =
  '[data-vibesite-richtext] p{margin-bottom:0.75em}' +
  '[data-vibesite-richtext] p:last-child{margin-bottom:0}' +
  '[data-vibesite-richtext] p:empty,[data-vibesite-richtext] p:has(>br:only-child){margin-bottom:0}';

// ============================================================================
// Provider Component
// ============================================================================

export interface VibesitesProviderProps {
  /** Site manifest configuration */
  manifest: SiteManifest;
  /** Property data from GraphQL */
  property: Property;
  /** Resolved component registry */
  registry: ResolvedRegistry;
  /** Current page context */
  page: PageContext;
  /**
   * All page paths that exist on this site. Forwarded into the runtime context
   * so the render-time cross-link verifier can drop blurb links to pages this
   * site doesn't ship. Optional — verification fails open when omitted.
   */
  pagePaths?: string[];
  /** Optional base tokens to merge with */
  baseTokens?: Partial<DesignTokens>;
  /** Content overrides from database (for preview/editing) */
  contentOverrides?: ContentOverridesMap;
  /** Content token schemas for components */
  contentSchemas?: Record<string, ComponentContentSchema>;
  /** Design token overrides from database (for preview/editing) */
  designOverrides?: DesignOverridesMap;
  /** Design token schemas for components */
  designSchemas?: Record<string, ComponentDesignSchema>;
  /** Slot group overrides from database (for preview/editing) */
  slotGroupOverrides?: SlotGroupOverridesMap;
  /** Collection binding overrides from database (for preview/editing) */
  collectionBindingOverrides?: CollectionBindingOverridesMap;
  /** Style overrides — direct CSS property adjustments per element */
  styleOverrides?: StyleOverridesMap;
  /** Block catalog metadata (serialized for inline editor use) */
  blockCatalogEntries?: Array<{ id: string; name: string; description: string; category: string }>;
  /** Company data for corporate (multi-property) sites */
  companyData?: CompanyData;
  /** Selected template context data (e.g., hyperlocal item for hyperlocal pages) */
  templateContext?: TemplateContext;
  /** Child components */
  children: ReactNode;
}

/**
 * Provider component that makes vibesites context available to all children.
 */
export function VibesitesProvider({
  manifest,
  property,
  registry,
  page,
  pagePaths,
  baseTokens,
  contentOverrides,
  contentSchemas,
  designOverrides,
  designSchemas,
  slotGroupOverrides,
  collectionBindingOverrides,
  styleOverrides,
  blockCatalogEntries,
  companyData,
  templateContext,
  children,
}: VibesitesProviderProps) {
  // Generate tokens from property data
  const tokens = useMemo(
    () => generateTokensFromProperty(property, baseTokens),
    [property, baseTokens]
  );

  // Generate CSS string for injection
  const cssString = useMemo(
    () => generateCSSString(tokens),
    [tokens]
  );

  // CSS for hyperlink styling tokens (color + underline) — emitted only when
  // the corresponding tokens have values at one of three scopes: site (package
  // tokens or _site design overrides), component slot (per-slot design
  // overrides), and per-token style overrides. When a token is null/unset at
  // every scope, no rule is emitted, preserving original UA-default link styling.
  const linkCssString = useMemo(() => {
    type UnderlineMode = 'underline' | 'none' | 'hover' | null;
    type LinkState = {
      hyperlink: boolean;
      hover: boolean;
      visited: boolean;
      underline: UnderlineMode;
    };
    const normalizeUnderline = (v: unknown): UnderlineMode => {
      return v === 'underline' || v === 'none' || v === 'hover' ? v : null;
    };
    const emptyState = (): LinkState => ({ hyperlink: false, hover: false, visited: false, underline: null });

    const rules: string[] = [];

    const rootState: LinkState = {
      hyperlink: typeof tokens?.color?.hyperlink === 'string' && !!tokens.color.hyperlink,
      hover: typeof tokens?.color?.linkHover === 'string' && !!tokens.color.linkHover,
      visited: typeof tokens?.color?.linkVisited === 'string' && !!tokens.color.linkVisited,
      underline: normalizeUnderline(tokens?.color?.linkUnderline),
    };

    const bySlot: Record<string, LinkState> = {};

    if (designOverrides && Object.keys(designOverrides).length > 0) {
      const scopeCtx = buildScopeContext(page);
      const allTokenPaths = new Set<string>();
      for (const rawKey of Object.keys(designOverrides)) {
        const sepIdx = rawKey.indexOf('::');
        allTokenPaths.add(sepIdx === -1 ? rawKey : rawKey.substring(sepIdx + 2));
      }

      for (const tokenPath of allTokenPaths) {
        const resolved = resolveScopedOverride(designOverrides, tokenPath, page?.path, scopeCtx);
        if (!resolved) continue;

        const parts = tokenPath.split('.');
        if (parts.length < 3 || parts[0] !== 'design') continue;
        const slot = parts[1];

        let resolvedValue: string | null = null;
        if (resolved.overrideType === 'FIELD_MAPPING' && resolved.mappedField) {
          const fieldValue = property ? (property as Record<string, unknown>)[resolved.mappedField] : null;
          resolvedValue = typeof fieldValue === 'string' ? fieldValue : null;
        } else if (resolved.value) {
          resolvedValue = resolved.value;
        }
        if (!resolvedValue) continue;

        const target = slot === '_site'
          ? rootState
          : (bySlot[slot] ??= emptyState());

        if (resolved.cssVariable === '--vibe-color-hyperlink') target.hyperlink = true;
        else if (resolved.cssVariable === '--vibe-color-link-hover') target.hover = true;
        else if (resolved.cssVariable === '--vibe-color-link-visited') target.visited = true;
        else if (resolved.cssVariable === '--vibe-color-link-underline') {
          const mode = normalizeUnderline(resolvedValue);
          if (mode) target.underline = mode;
        }
      }
    }

    const appendRules = (selector: string, state: LinkState) => {
      if (state.hyperlink) rules.push(`${selector} a { color: var(--vibe-color-hyperlink); }`);
      if (state.hover) rules.push(`${selector} a:hover { color: var(--vibe-color-link-hover); }`);
      if (state.visited) rules.push(`${selector} a:visited { color: var(--vibe-color-link-visited); }`);
      if (state.underline === 'underline') {
        rules.push(`${selector} a { text-decoration-line: underline; }`);
      } else if (state.underline === 'none') {
        rules.push(`${selector} a { text-decoration-line: none; }`);
      } else if (state.underline === 'hover') {
        rules.push(`${selector} a { text-decoration-line: none; }`);
        rules.push(`${selector} a:hover { text-decoration-line: underline; }`);
      }
    };

    const hasAny = (s: LinkState) => s.hyperlink || s.hover || s.visited || s.underline !== null;

    if (hasAny(rootState)) {
      appendRules('[data-vibesite-richtext]', rootState);
    }

    for (const [slot, state] of Object.entries(bySlot)) {
      if (hasAny(state)) {
        appendRules(`[data-vibesite-slot="${slot}"] [data-vibesite-richtext]`, state);
      }
    }

    if (styleOverrides && Object.keys(styleOverrides).length > 0) {
      const scopeCtx = buildScopeContext(page);
      const allPaths = new Set<string>();
      for (const rawKey of Object.keys(styleOverrides)) {
        const sepIdx = rawKey.indexOf('::');
        allPaths.add(sepIdx === -1 ? rawKey : rawKey.substring(sepIdx + 2));
      }

      for (const tokenPath of allPaths) {
        const resolved = resolveScopedOverride(styleOverrides, tokenPath, page?.path, scopeCtx);
        if (!resolved) continue;
        const props = resolved.properties || {};
        const state: LinkState = {
          hyperlink: typeof props['--vibe-color-hyperlink'] === 'string' && !!props['--vibe-color-hyperlink'],
          hover: typeof props['--vibe-color-link-hover'] === 'string' && !!props['--vibe-color-link-hover'],
          visited: typeof props['--vibe-color-link-visited'] === 'string' && !!props['--vibe-color-link-visited'],
          underline: normalizeUnderline(props['--vibe-color-link-underline']),
        };
        if (hasAny(state)) {
          appendRules(`[data-vibesite-token="${tokenPath}"] [data-vibesite-richtext]`, state);
        }
      }
    }

    return rules.join('\n');
  }, [tokens, designOverrides, styleOverrides, property, page]);

  // Generate override CSS string for design token overrides
  // Site-level overrides (design._site.*) apply to :root
  // Component-level overrides apply via [data-vibesite-slot="ComponentName"] selectors
  // Supports both VALUE (direct value) and FIELD_MAPPING (live link to property field) modes
  // Page-scoped overrides use composite keys ("pageAlias::tokenPath") and take precedence over global
  const overrideCssString = useMemo(() => {
    if (!designOverrides || Object.keys(designOverrides).length === 0) {
      return '';
    }
    
    // Build effective override map using the full scope hierarchy.
    // Collect all unique token paths and resolve each through the scope system.
    const scopeCtx = buildScopeContext(page);
    const allTokenPaths = new Set<string>();
    for (const rawKey of Object.keys(designOverrides)) {
      const sepIdx = rawKey.indexOf('::');
      allTokenPaths.add(sepIdx === -1 ? rawKey : rawKey.substring(sepIdx + 2));
    }
    const effective = new Map<string, DesignOverride>();
    for (const tokenPath of allTokenPaths) {
      const resolved = resolveScopedOverride(designOverrides, tokenPath, page?.path, scopeCtx);
      if (resolved) effective.set(tokenPath, resolved);
    }
    
    // Separate site-level vs component-level overrides
    const siteOverrides: Array<{ cssVariable: string; value: string }> = [];
    const bySlot: Record<string, Array<{ cssVariable: string; value: string }>> = {};
    
    for (const [tokenPath, override] of effective) {
      const parts = tokenPath.split('.');
      if (parts.length >= 3 && parts[0] === 'design') {
        const slot = parts[1];
        
        let resolvedValue: string | null = null;
        if (override.overrideType === 'FIELD_MAPPING' && override.mappedField) {
          const fieldValue = property ? (property as Record<string, unknown>)[override.mappedField] : null;
          resolvedValue = typeof fieldValue === 'string' ? fieldValue : null;
        } else if (override.value) {
          resolvedValue = override.value;
        }
        
        if (resolvedValue) {
          if (slot === '_site') {
            siteOverrides.push({ cssVariable: override.cssVariable, value: resolvedValue });
          } else {
            if (!bySlot[slot]) bySlot[slot] = [];
            bySlot[slot].push({ cssVariable: override.cssVariable, value: resolvedValue });
          }
        }
      }
    }
    
    // Recompute contrast text tokens when a background token is overridden.
    // textOnPrimary/textOnSecondary are auto-derived from the property colors at
    // token-generation time, so an editor override of primary/secondary would
    // otherwise leave the stale text color behind (e.g. black → white primary
    // keeps white text). Skipped when the same scope explicitly overrides the
    // text token, or when the value isn't a parseable hex color.
    const CONTRAST_PAIRS: Array<[string, string]> = [
      ['--vibe-color-primary', '--vibe-color-text-on-primary'],
      ['--vibe-color-secondary', '--vibe-color-text-on-secondary'],
    ];
    const appendContrastCompanions = (list: Array<{ cssVariable: string; value: string }>) => {
      for (const [bgVar, textVar] of CONTRAST_PAIRS) {
        const bg = list.find(o => o.cssVariable === bgVar);
        if (!bg) continue;
        if (list.some(o => o.cssVariable === textVar)) continue;
        if (!/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(bg.value.trim())) continue;
        list.push({ cssVariable: textVar, value: getContrastTextColor(bg.value.trim()) });
      }
    };
    appendContrastCompanions(siteOverrides);
    for (const slotOverrides of Object.values(bySlot)) {
      appendContrastCompanions(slotOverrides);
    }

    const cssRules: string[] = [];

    // Site-level overrides on :root (after base tokens, before component-level)
    if (siteOverrides.length > 0) {
      const vars = siteOverrides.map(o => `  ${o.cssVariable}: ${o.value};`).join('\n');
      cssRules.push(`:root {\n${vars}\n}`);
    }

    // Component-level overrides (more specific selectors override site-level)
    for (const [slot, slotOverrides] of Object.entries(bySlot)) {
      const vars = slotOverrides.map(o => `  ${o.cssVariable}: ${o.value};`).join('\n');
      cssRules.push(`[data-vibesite-slot="${slot}"] {\n${vars}\n}`);
    }
    
    return cssRules.join('\n\n');
  }, [designOverrides, property, page]);

  // Build flat token definition index from contentSchemas for schema-aware hooks.
  // This lets useContentToken and EditableToken derive defaults from the schema
  // without callers needing to pass them explicitly.
  const tokenDefinitionIndex = useMemo(() => {
    if (!contentSchemas) return undefined;
    const map: Record<string, ContentTokenDefinition> = {};
    for (const schema of Object.values(contentSchemas)) {
      if (!schema.tokens) continue;
      for (const [path, def] of Object.entries(schema.tokens)) {
        map[path] = def;
      }
    }
    return map;
  }, [contentSchemas]);

  // Build runtime context
  const context: RuntimeContext = useMemo(
    () => ({
      manifest,
      property,
      tokens,
      registry,
      page,
      pagePaths,
      contentOverrides,
      contentSchemas,
      tokenDefinitionIndex,
      designOverrides,
      designSchemas,
      slotGroupOverrides,
      collectionBindingOverrides,
      styleOverrides,
      blockCatalogEntries,
      companyData,
      templateContext,
    }),
    [manifest, property, tokens, registry, page, pagePaths, contentOverrides, contentSchemas, tokenDefinitionIndex, designOverrides, designSchemas, slotGroupOverrides, collectionBindingOverrides, styleOverrides, blockCatalogEntries, companyData, templateContext]
  );

  return (
    <VibesitesContext.Provider value={context}>
      {/* Restore richtext paragraph spacing zeroed by Tailwind Preflight (scoped
          to the richtext hook; first so themes/overrides can still win) */}
      <style dangerouslySetInnerHTML={{ __html: RICHTEXT_BASE_CSS }} />
      {/* Inject CSS variables */}
      <style dangerouslySetInnerHTML={{ __html: cssString }} />
      {/* Inject design token overrides (after base tokens to take precedence) */}
      {overrideCssString && (
        <style dangerouslySetInnerHTML={{ __html: overrideCssString }} />
      )}
      {/* Inject hyperlink color rules (must come after overrideCssString so
          slot- and token-scoped variable declarations are in effect first) */}
      {linkCssString && (
        <style dangerouslySetInnerHTML={{ __html: linkCssString }} />
      )}
      {/* Mount the runtime event bus + DOM capture for this page. */}
      <EventsProvider>{children}</EventsProvider>
    </VibesitesContext.Provider>
  );
}

// ============================================================================
// Context Hook
// ============================================================================

/**
 * Hook to access the vibesites runtime context.
 */
export function useVibesites(): RuntimeContext {
  const context = useContext(VibesitesContext);
  
  if (!context) {
    throw new Error(
      'useVibesites must be used within a VibesitesProvider. ' +
      'Wrap your app with <VibesitesProvider>.'
    );
  }

  return context;
}

/**
 * Hook to access property data.
 */
export function useProperty(): Property {
  return useVibesites().property;
}

/**
 * Hook to access design tokens.
 */
export function useTokens(): DesignTokens {
  return useVibesites().tokens;
}

/**
 * Hook to access the component registry.
 */
export function useRegistry(): ResolvedRegistry {
  return useVibesites().registry;
}

/**
 * Hook to access page context.
 */
export function usePage(): PageContext {
  return useVibesites().page;
}

/**
 * Hook to access the site manifest.
 */
export function useManifest(): SiteManifest {
  return useVibesites().manifest;
}

/**
 * Hook to access content overrides.
 */
export function useContentOverrides(): ContentOverridesMap {
  return useVibesites().contentOverrides || {};
}

/**
 * Hook to access design overrides.
 */
export function useDesignOverrides(): DesignOverridesMap {
  return useVibesites().designOverrides || {};
}

/**
 * Hook to access style overrides.
 */
export function useStyleOverrides(): StyleOverridesMap {
  return useVibesites().styleOverrides || {};
}

/**
 * Hook to resolve a single style override with page-scope awareness.
 */
export function useScopedStyleOverride(path: string): StyleOverride | undefined {
  const { styleOverrides, page } = useVibesites();
  const scopeCtx = buildScopeContext(page);
  return resolveScopedOverride(styleOverrides, path, page?.path, scopeCtx);
}

/**
 * Hook to resolve a single content override with page-scope awareness.
 */
export function useScopedContentOverride(path: string): ContentOverride | undefined {
  const { contentOverrides, page } = useVibesites();
  const scopeCtx = buildScopeContext(page);
  return resolveScopedOverride(contentOverrides, path, page?.path, scopeCtx);
}

/**
 * Hook to access block catalog entries (serialized metadata for inline editor).
 */
export function useBlockCatalogEntries(): Array<{ id: string; name: string; description: string; category: string }> {
  return useVibesites().blockCatalogEntries || [];
}

/**
 * Hook to access company data for corporate (multi-property) sites.
 * Returns null for single-property sites.
 */
export function useCompanyData(): CompanyData | undefined {
  return useVibesites().companyData;
}

/**
 * Access the current template context data (e.g., selected hyperlocal item).
 * Returns undefined when no template context is set.
 */
export function useTemplateContext(): TemplateContext | undefined {
  return useVibesites().templateContext;
}

/**
 * Hook to look up a content token definition from the contentSchema.
 * Returns undefined when contentSchemas are not provided or the path is not found.
 */
export function useTokenDefinition(path: string): ContentTokenDefinition | undefined {
  return useVibesites().tokenDefinitionIndex?.[path];
}

// ============================================================================
// Page-Scoped Override Helpers
// ============================================================================

/**
 * Normalize a page path for use as a page_scope key.
 * Strips leading/trailing slashes for consistent matching.
 */
function normalizePageScope(path: string | undefined): string {
  if (!path) return '';
  return path.replace(/^\/+|\/+$/g, '');
}

/**
 * Build a ScopePageContext from the runtime PageContext.
 * Used by all scoped override resolution to support the full 5-level hierarchy.
 */
export function buildScopeContext(page: PageContext | undefined): ScopePageContext {
  const alias = page?.alias || '';
  return {
    alias,
    path: page?.path || '/',
    template: page?.template,
    baseAlias: page?.baseAlias ?? (alias.includes('--') ? alias.split('--')[0] : undefined),
  };
}

/**
 * Resolve an override from a map that may contain scoped entries.
 *
 * Supports the full 5-level scope hierarchy:
 *   page > path > template-alias > template-group > global
 *
 * See vibesites-core/runtime/src/scope.ts for the canonical scope documentation.
 */
function resolveScopedOverride<T>(
  overrides: Record<string, T> | undefined,
  tokenPath: string,
  pagePath: string | undefined,
  pageCtx?: ScopePageContext,
): T | undefined {
  if (!overrides) return undefined;

  // If a full ScopePageContext is available, use the new multi-level resolver
  if (pageCtx) {
    return resolveScopedOverrideV2(overrides, tokenPath, pageCtx);
  }

  // Legacy fallback: page-alias + global only
  const scope = normalizePageScope(pagePath);
  if (scope) {
    const scoped = overrides[`${scope}::${tokenPath}`];
    if (scoped) return scoped;
  }
  return overrides[tokenPath];
}

// ============================================================================
// Content Token Resolution
// ============================================================================

/**
 * Options for resolving a content token.
 * 
 * Tokens are unified - each can be in field mode or custom mode:
 * - If defaultField is set → defaults to field mode (reads from property)
 * - If only defaultValue is set → defaults to custom mode
 */
export interface ContentTokenOptions {
  /** Default property field to read from (if set, defaults to field mode) */
  defaultField?: string;
  /** Default value if no override or field value exists */
  defaultValue?: string;
}

/**
 * Hook to resolve a content token value with override support.
 * 
 * All tokens are unified - each can be in field mode or custom mode.
 * 
 * When options are omitted, defaults are derived from the contentSchema
 * (via tokenDefinitionIndex) so callers don't need to duplicate them.
 * Explicit options always take precedence over schema-derived values.
 * 
 * Resolution priority:
 * 1. If VALUE override exists → use customValue
 * 2. If FIELD_MAPPING override exists → read from that property field
 * 3. If defaultField is set → read from that property field
 * 4. Fall back to defaultValue
 * 
 * @param tokenPath - The token path (e.g., 'hero.title')
 * @param options - Token resolution options (optional — derived from contentSchema when omitted)
 * @returns The resolved value or null
 * 
 * @example
 * // Schema-aware: defaults derived from contentSchema automatically
 * const title = useContentToken('hero.title');
 * 
 * @example
 * // Explicit options still work (take precedence over schema, but less desirable)
 * const ctaText = useContentToken('hero.cta_text', {
 *   defaultValue: 'Schedule a Tour',
 * });
 */
export function useContentToken(
  tokenPath: string,
  options: ContentTokenOptions = {}
): string | null {
  const { property, contentOverrides, page, tokenDefinitionIndex } = useVibesites();
  const schemaDef = tokenDefinitionIndex?.[tokenPath];
  const scopeCtx = buildScopeContext(page);
  const override = resolveScopedOverride(contentOverrides, tokenPath, page?.path, scopeCtx) ?? null;

  return resolveContentTokenValue(property, override, {
    defaultField: options.defaultField ?? schemaDef?.defaultField,
    defaultValue: options.defaultValue ?? schemaDef?.defaultValue,
  });
}

/**
 * Hook to resolve multiple content tokens at once.
 * 
 * Uses the same unified resolution logic as useContentToken for each token.
 * 
 * @param tokens - Object mapping keys to token options
 * @returns Object with the same keys containing resolved values
 * 
 * @example
 * const content = useContentTokens({
 *   title: { defaultField: 'property_home_hero_title' },
 *   ctaText: { defaultValue: 'Schedule a Tour' },
 * });
 * // content.title, content.ctaText
 */
export function useContentTokens<T extends Record<string, ContentTokenOptions>>(
  tokens: T
): Record<keyof T, string | null> {
  const { property, contentOverrides, page, tokenDefinitionIndex } = useVibesites();
  const scopeCtx = buildScopeContext(page);

  const result = {} as Record<keyof T, string | null>;

  for (const [key, options] of Object.entries(tokens) as [keyof T, ContentTokenOptions][]) {
    const tokenPath = key as string;
    const schemaDef = tokenDefinitionIndex?.[tokenPath];
    const override = resolveScopedOverride(contentOverrides, tokenPath, page?.path, scopeCtx) ?? null;
    result[key] = resolveContentTokenValue(property, override, {
      defaultField: options.defaultField ?? schemaDef?.defaultField,
      defaultValue: options.defaultValue ?? schemaDef?.defaultValue,
    });
  }

  return result;
}

// ============================================================================
// Design Token Resolution
// ============================================================================

/**
 * Options for resolving a design token.
 */
export interface DesignTokenOptions {
  /** CSS variable name (e.g., '--vibe-color-primary') */
  cssVariable: string;
  /** Default value if no override exists */
  defaultValue?: string;
}

/**
 * Hook to resolve a design token value with override support.
 * 
 * @param tokenPath - The token path (e.g., 'hero.background_color')
 * @param options - Token resolution options
 * @returns The resolved CSS variable value or the default
 * 
 * @example
 * const primaryColor = useDesignToken('hero.primary_color', {
 *   cssVariable: '--vibe-color-primary',
 *   defaultValue: '#000000',
 * });
 */
export function useDesignToken(
  tokenPath: string,
  options: DesignTokenOptions
): string {
  const { designOverrides, page } = useVibesites();
  const { cssVariable: _cssVariable, defaultValue = '' } = options;
  const scopeCtx = buildScopeContext(page);
  
  const override = resolveScopedOverride(designOverrides, tokenPath, page?.path, scopeCtx);
  
  if (override && override.value) {
    return override.value;
  }
  
  return defaultValue;
}

/**
 * Hook to get a CSS variable reference string for use in inline styles.
 * The returned string can be used directly in style props.
 * 
 * @param tokenPath - The token path (e.g., 'hero.background_color')
 * @param cssVariable - The CSS variable name
 * @param fallback - Optional fallback value
 * @returns CSS var() function string
 * 
 * @example
 * const bgColor = useDesignTokenVar('hero.bg', '--vibe-color-primary', '#000');
 * // Returns: "var(--vibe-color-primary, #000)" or the override value
 */
export function useDesignTokenVar(
  tokenPath: string,
  cssVariable: string,
  fallback?: string
): string {
  const { designOverrides, page } = useVibesites();
  const scopeCtx = buildScopeContext(page);
  
  const override = resolveScopedOverride(designOverrides, tokenPath, page?.path, scopeCtx);
  
  // If there's an override, return the direct value
  if (override && override.value) {
    return override.value;
  }
  
  // Otherwise return the CSS var() function
  return fallback 
    ? `var(${cssVariable}, ${fallback})`
    : `var(${cssVariable})`;
}

// ============================================================================
// Slot Group Resolution
// ============================================================================

export { resolveFieldPath, resolveContentTokenValue, resolveSlotGroupFields } from '../resolve';
export type { SlotGroupResult } from '../resolve';

/**
 * Hook to resolve a slot group (compound token with multiple related fields).
 * 
 * Resolution priority for each field:
 * 1. Individual field override (VALUE) → use customValue
 * 2. Individual field override (FIELD_MAPPING) → resolve mappedField
 * 3. Source index override → remap source-bound fields to new index
 * 4. Default → use defaultField from definition
 * 
 * @param groupPath - The slot group path (e.g., 'box.1', 'feature.hero')
 * @param definition - The slot group definition from contentSchema
 * @returns Object with resolved values for each field in the group
 * 
 * @example
 * const contentSchema = {
 *   slotGroups: {
 *     'box.1': {
 *       label: 'Box 1',
 *       fields: {
 *         title: { label: 'Title', type: 'text', defaultField: 'amenities[0].title' },
 *         image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
 *       },
 *       sourceBinding: {
 *         collection: 'amenities',
 *         defaultIndex: 0,
 *         fieldMappings: { title: 'title' },
 *       },
 *     },
 *   },
 * };
 * 
 * function FeatureBox() {
 *   const box1 = useSlotGroup('box.1', contentSchema.slotGroups['box.1']);
 *   return (
 *     <div>
 *       <h3>{box1.title}</h3>
 *       <img src={box1.image} />
 *     </div>
 *   );
 * }
 */
export function useSlotGroup(
  groupPath: string,
  definition: SlotGroupDefinition
): SlotGroupResult {
  const { property, slotGroupOverrides, page } = useVibesites();
  const scopeCtx = buildScopeContext(page);
  const override = resolveScopedOverride(slotGroupOverrides, groupPath, page?.path, scopeCtx) ?? null;
  return resolveSlotGroupFields(property, override, definition);
}

/**
 * Hook to access slot group overrides directly.
 */
export function useSlotGroupOverrides(): SlotGroupOverridesMap {
  return useVibesites().slotGroupOverrides || {};
}

// ============================================================================
// Collection Binding Resolution
// ============================================================================

/**
 * Hook to resolve a collection binding with override support.
 *
 * Applies hide / order / custom inject / limit. ID fields (`hiddenIds`,
 * `orderedIds`) supersede legacy index fields when present. See
 * `applyCollectionBindingOverride` for the full contract.
 */
export function useCollectionBinding<T = unknown>(
  bindingPath: string,
  options: {
    collection: keyof Property;
    defaultLimit?: number;
    filter?: Record<string, string>;
  }
): T[] {
  const { property, collectionBindingOverrides, page } = useVibesites();
  const scopeCtx = buildScopeContext(page);
  const override = resolveScopedOverride(collectionBindingOverrides, bindingPath, page?.path, scopeCtx);

  // The contentSchema often omits filter/defaultLimit (authors duplicate it by
  // convention), so for the editor, this hook call is the source of truth —
  // broadcast the resolved binding def to the parent frame.
  const filterKey = options.filter ? JSON.stringify(options.filter) : '';
  const bindingDefinition = useMemo(
    () => ({
      collection: options.collection as CollectionBindingDefinition['collection'],
      defaultLimit: options.defaultLimit,
      filter: options.filter,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.collection, options.defaultLimit, filterKey],
  );
  useReportedRuntimeDefinition('collectionBinding', bindingPath, bindingDefinition, IS_IN_EDITOR_FRAME);

  // Get base collection — prefer live data from the edge worker when available
  const liveData = useLiveCollection<T>(options.collection as string);
  const baseCollection = liveData ?? property[options.collection];

  // Memoized so consumers get a stable array identity between unrelated renders.
  return useMemo(() => {
    let items: T[] = Array.isArray(baseCollection) ? [...baseCollection] as T[] : [];
    if (options.filter) {
      const entries = Object.entries(options.filter);
      items = items.filter(item =>
        entries.every(([k, v]) => (item as Record<string, unknown>)[k] === v),
      );
    }

    return applyCollectionBindingOverride(
      items as Array<Record<string, unknown>>,
      override,
      { defaultLimit: options.defaultLimit },
    ) as T[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCollection, override, filterKey, options.defaultLimit]);
}

/**
 * Hook to access collection binding overrides directly.
 */
export function useCollectionBindingOverrides(): CollectionBindingOverridesMap {
  return useVibesites().collectionBindingOverrides || {};
}

